import { useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocketContext } from '../../SocketContext';
import requestClient from '../Utility/RequestClient';
import { setIncidents } from '../../Store/features/openAIIncidents/openAIIncidentsSlice';
import { Outlet } from 'react-router-dom';
import Container from '../Utility/Container';
import WaitForActivation from '../WaitForActivation/WaitForActivation';
import Error from '../../Error';
import { WarningIcon } from '@phosphor-icons/react';
import ChatListToggleButton from '../ChatList/ChatListToggleButton';
import { ChatList } from '../ChatList/ChatList/ChatList';
import NavigationHeader from '../NavigationHeader/NavigationHeader';
import MessageSource from '../MessageSource/MessageSource';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import { UserSettings } from '../../Types';
import useModal from '../Modal/useModal';
import DemoModal from '../Demo/DemoModal';
import { getBreakpoint } from '../../utils/breakpoint';

function Layout() {
    const navigate = useNavigate();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const dispatch = useAppDispatch();
    const demo = useAppSelector((state: RootState) => state.environment.demoInstance as boolean);
    
    const handleNavigate = useCallback(
        (path: string) => {
            navigate(path);
        },
        [navigate],
    );
    const error = useAppSelector((state: RootState) => state.error.error);
    const errorRef = useRef(error);
    const hasOpenAIIncidents = useAppSelector((state: RootState) => state.openAIIncidents.hasIncidents);
    const incidents = useAppSelector((state: RootState) => state.openAIIncidents.incidents);
    const { addMessageHandler } = useContext(SocketContext);
    const modal = useModal();
    useEffect(() => {
        errorRef.current = error;
    }, [error]);

    useEffect(() => {
        setTimeout(() => {
            if (!errorRef.current) {
                localStorage.setItem('errorRedirectCounter', '0');
            }
        }, 5000);
    }, []);

    useEffect(() => {
        if (
            document.location.search.includes('accountIdentifier=') &&
            document.location.search.includes('authenticationProviderName=') &&
            document.location.search.includes('token=')
        ) {
            handleNavigate(document.location.pathname);
        }
    }, [handleNavigate]);

    useEffect(() => {
        requestClient.get(`/settings/openaistatus`).then((response: any) => {
            dispatch(setIncidents(response.data.incidents));
        });

        addMessageHandler('openaiIncidentsUpdated', (data) => {
            requestClient.get(`/settings/openaistatus`).then((response: any) => {
                dispatch(setIncidents(response.data.incidents));
            });
        });
    }, [addMessageHandler, dispatch]);

    const openIncidentModal = () => {
        modal.open({
            title: i18n.translate({ id: 'openAIIncidents.title', sourceName: 'Layout' }),
            content: `
                ${i18n.translate({ id: 'openAIIncidents.description', sourceName: 'Layout' })}
                ${incidents.reduce((acc: string, incident: any) => {
                    return `${acc}<code>${incident.title}</code><br />`;
                }, '')}`,
            primaryButton: {
                text: i18n.translate({ id: 'openAIIncidents.button', sourceName: 'Layout' }),
                onClick: () => window.open('https://status.openai.com/', '_blank'),
            },
            size: 'medium',
        });
    };

    return userSettings.isLoggedIn ? (
        <div className={`flex admin-layout ${windowWidth < getBreakpoint('xl') ? 'flex-row' : 'flex-row'}`}>
            {error && <Error />}
            <>
                {hasOpenAIIncidents && (
                    <button
                        className="fixed z-50 top-[150px] xl:top-[170px] right-0 bg-warning rounded-l-full text-white pl-[18px] md:pl-[27px] pr-2 py-2 md:pr-4 md:py-4 text-right hover:cursor-pointer hover:no-underline"
                        onClick={openIncidentModal}
                    >
                        <h6 className="text-[16px] leading-[16px] md:text-[18px] md:leading-[18px] text-white">
                            <WarningIcon size={24} />
                        </h6>
                    </button>
                )}

                {demo && userSettings.availableDemoAmount >= 0 && (
                    <div
                        className={`fixed z-50 ${'top-[250px] xl:top-[270px]'} right-0 bg-primary rounded-l-full text-white pl-[18px] md:pl-[27px] pr-2 py-2 md:pr-4 md:py-4 text-right hover:cursor-pointer hover:no-underline`}
                        onClick={() =>
                            modal.open({
                                title: i18n.translate({
                                    id: 'modal.headline',
                                    sourceName: 'Demo',
                                }),
                                size: 'large',
                                content: <DemoModal />,
                            })
                        }
                    >
                        <h6 className="text-layout-textColorOnPrimary text-[16px] leading-[16px] md:text-[18px] md:leading-[18px]">
                            {windowWidth >= getBreakpoint('md') &&
                                i18n.translate({
                                    id: 'remainingCredit',
                                    sourceName: 'Layout',
                                })}
                            {windowWidth >= getBreakpoint('md') && ':'}
                            {userSettings.availableDemoAmountFormatted}
                        </h6>
                    </div>
                )}

                <div>
                    <ChatListToggleButton />
                    {windowWidth >= getBreakpoint('xl') && <ChatList />}
                </div>
                <div className={`w-full admin-window transition-all duration-300 flex`}>
                    {!settings.registrationEnabled || userSettings.roles.includes('Avency.Jwt:ActiveUser') ? (
                        <>
                            <div className="chat-layout relative flex grow">
                                <div className="chat-window grow w-full">
                                    <NavigationHeader />
                                    <div className="flex">
                                        <div className="flex-grow">
                                            <Outlet />
                                        </div>
                                        <MessageSource />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <WaitForActivation />
                    )}
                </div>
            </>
        </div>
    ) : (
        <Container>
            <h1>{i18n.translate({ id: 'title', sourceName: 'Layout' })}</h1>
        </Container>
    );
}

export default Layout;
